import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Link,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import Flex from "./Flex";
// import SquareIcon from "@mui/icons-material/Square";
// import patientImg from "../assets/patientImg.png";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
// import VisibilityIcon from "@mui/icons-material/Visibility";
import { CheckSharp, Close } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { Elements } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GENDER_RECORD } from "../constants";
import { INSURANCE_TYPE_MAPPER } from "../dummy-data/add-new-patient";
import { Action, Status } from "../dummy-data/appointment-list-data-scheduling";
import useDisclosure from "../hooks/use-disclosure";
import { useReduceDisclosure } from "../hooks/use-reduce-disclosure";
import { useSnackbar } from "../hooks/useSnackbar";
import ViewEncounterSummaryDrawer from "../pages/app/provider/billings/encounter/ViewEncounterSummaryDrawer";
import SendBulkEmailModal from "../pages/app/provider/communications/email/SendBulkEmailModal";
import { ContactDialModal } from "../pages/app/provider/dashboard/twilio/ContactDial";
import {
  APPOINTMENT_STATUS_RECORD,
  COLOR_MAPPER,
} from "../pages/app/provider/scheduling/appointment/AppointmentScheduling";
import UpsertEditCheckInPatient from "../pages/app/provider/scheduling/appointment/upsertEditCheckInPatient";
import { useAdditionalFormSend } from "../queries/appointment/use-additional-form-send";
import { getStripePromise } from "../queries/stripe/use-stripe";
import { useUpdateAppointmentStatus } from "../queries/use-change-appointment-status";
import { useGetAppointment } from "../queries/use-get-appointment";
import { useGetPatient } from "../queries/use-get-patient-uuid";
import { usePaymentLinkUrl } from "../queries/use-payment-link-url";
import { setCallData } from "../redux/feature/join-room/JoinRoomSlice";
import { store } from "../redux/store";
import { formatDate, formatTime } from "../utils/date-time-utility";
import {
  DEFAULT_BG_COLOR_2,
  DEFAULT_FONTSIZE,
  DEFAULT_USER_SRC,
  // camelCaseToTitle,
  formatPhoneNumber,
  getAge,
  getErrorSnackbar,
  getPatientVisitNoteUrl,
  getSuccessSnackbar,
  getXtenantId,
  replaceUnderScoreAddSpace,
  roleFilter,
  showToastOnApiCall,
} from "../utils/utils";
import AddInsuranceDrower from "./AddInsuranceDrawer";
import CustomAccordion from "./CustomAccordion";
import EligibilityVerification from "./EligibilityVerification";
import LoadingOverlay from "./LoadingOverlay";
import NumberInput from "./NumberInput";
import PaymentLinkDetails, { hasPaymentLink } from "./PaymentLinkDetails";
import ProfileViewer from "./ProfileViewer";
import SideSheetDrawer from "./SidesheetDrawer";
import Text from "./Text";
import StyledButtonV2 from "./app/StyledButtonV2";
import PaymentDrawer from "./app/provider/billing/payment/PaymentDrawer";
import { CHART_COLORS } from "./app/provider/patient/chart";
import IntakeForms from "./intakeForms";
import { useRoleController } from "./useRoleController";
import { getStoredRealm } from "@/utils/auth-storage";
import { AppointmentAdditionalForms } from "@/components/appointment-additional-forms";
import { FORMS_AND_ASSESSMENTS_BY_APPOINTMENT_QUERY_KEY } from "@/queries/patient-chart/forms/use-get-forms-and-assessments-by-appointment";

interface SideDrawerProps {
  open: boolean;
  onClose?: () => void;
  appointmentUuid: string | undefined;
  activeTab?: string;
  handleTabChange?: (v: string) => void;
  openChangeStatus: (statusChanged: "CANCELLED" | "NO_SHOW") => void;
  openRescheduleAppointment: (appointmentData?: any) => void | undefined;
  openCheckIn: () => void;
}

const ViewAppointment: React.FC<SideDrawerProps> = ({
  open,
  onClose,
  openChangeStatus,
  appointmentUuid,
  openRescheduleAppointment,
  openCheckIn: _,
}) => {
  const { mutateAsync: updateStatus } = useUpdateAppointmentStatus();
  // const { mutateAsync: checkEligibility } = useCheckEligibility();
  const {
    data: appointmentData,
    isLoading: loadingAppointment,
    refetch: refetchByIdData,
  }: any = useGetAppointment({
    appointmentUuid: appointmentUuid,
    providerGroupId: getStoredRealm() ?? "",
  });
  const queryClient = useQueryClient();
  const [expandedSections, setExpandedSections] = useState<boolean[]>([
    true,
    true,
  ]);
  const [amount, setAmount] = useState<string>("");

  useEffect(() => {
    const data = appointmentData?.data;
    // Once a link has been sent the appointment carries the amount it was
    // generated for. Prefill it so a resend does not need the amount typed
    // again — the refetch after each send used to blank this field.
    const prefillAmount = hasPaymentLink(data)
      ? data?.paymentLinkAmount
      : data?.paymentType === "INSURANCE"
        ? data?.copayAmount
        : "";

    setAmount(
      prefillAmount === null || prefillAmount === undefined
        ? ""
        : prefillAmount.toString()
    );
    return () => {
      setAmount("");
    };
  }, [appointmentData?.data, open]);

  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIntakeForms, setSelectedIntakeForms] = useState<any[]>();
  const [selectedAssessments, setSelectedAssessments] = useState<any[]>();
  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );
  const { hasUpdate: hasUpdateInsurance, hasView: hasViewInsurance } =
    useRoleController("Insurance");
  const { hasUpdate: hasUpdateIntake, hasView: hasViewIntake } =
    useRoleController("Intake");
  const { hasUpdate: hasUpdateRescheduleAndCancel } = useRoleController(
    "Reschedule and Cancel appointment"
  );
  const { hasUpdate: hasUpdateStartEncounter } = useRoleController(
    "Encounter - Virtual"
  );
  const { mutateAsync: sendPaymentLink } = usePaymentLinkUrl();

  const handleSendPaymentLink = () => {
    if (!amount) {
      showSnackbar({
        type: "error",
        message: "Amount is required for payment link",
      });
      return;
    }

    showToastOnApiCall({
      apiCall: sendPaymentLink({
        appointmentId: appointmentData?.data?.uuid ?? "",
        amount: Number(amount),
        resend: !!appointmentData?.data?.paymentLinkSent,
        xTENANTID: getXtenantId(),
        paymentCategory:
          appointmentData?.data?.paymentType === "INSURANCE"
            ? "COPAY"
            : "SELF_PAY",
      }),
      showSnackbar,
      onSave: refetchByIdData,
    });
  };

  const label =
    APPOINTMENT_STATUS_RECORD?.[appointmentData?.data?.status as Status];
  // const { data: intakeForms }: any = useCustomFormList({
  //   pagination: DEF_PAGINATION,
  //   searchKey: "",
  //   formType: "INTAKE_FORM",
  //   providerGroupId: getStoredRealm() ?? undefined,
  // });
  const encounterSummaryDisc = useDisclosure();
  const contactDialDisclosure = useDisclosure(false);
  const sendMailDisclosure = useDisclosure(false);
  const payDisc = useDisclosure(false);

  const handleCallPatient = () => {
    const patientPhone = appointmentData?.data?.patientPhone;
    if (!patientPhone) {
      showSnackbar({ type: "error", message: "No phone number available" });
      return;
    }
    if (!appointmentData?.data?.callConsent) {
      showSnackbar({
        type: "error",
        message: "Patient has not consented to phone calls",
      });
      return;
    }
    contactDialDisclosure.open(appointmentData?.data?.patientId);
  };

  const handleEmailPatient = () => {
    const patientEmail = appointmentData?.data?.patientEmail;
    if (!patientEmail) {
      showSnackbar({ type: "error", message: "No email address available" });
      return;
    }
    if (!appointmentData?.data?.emailConsent) {
      showSnackbar({
        type: "error",
        message: "Patient has not consented to email communication",
      });
      return;
    }
    sendMailDisclosure.open([
      {
        uuid: appointmentData?.data?.patientId ?? "",
        name: appointmentData?.data?.patientName ?? "",
      },
    ]);
  };

  const [addPatientInsuranceDisclosure, editDemoDisclosure] =
    useReduceDisclosure(2);

  const showSnackbar = useSnackbar();

  const { data: patientData } = useGetPatient(
    appointmentData?.data?.patientId,
    !open
  );

  const primaryIns = appointmentData?.data?.patientPrimaryInsurance;

  const { mutateAsync: sendAdditionalForm } = useAdditionalFormSend();

  const handleSendAdditionalForm = () => {
    sendAdditionalForm({
      appointmentId: appointmentData?.data?.uuid ?? "",
      customForms: selectedIntakeForms?.map((it: any) => it?.value)?.join(","),
      assessments: selectedAssessments?.map((it: any) => it?.value).join(","),
      xTENANTID: getXtenantId(),
    })
      .then((res) => {
        setSelectedIntakeForms([]);
        setSelectedAssessments([]);
        queryClient.invalidateQueries({
          queryKey: [FORMS_AND_ASSESSMENTS_BY_APPOINTMENT_QUERY_KEY],
        });
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const ACTIVE_COMP: Record<string, ReactNode> = {
    appointment: (
      <Box ml={1}>
        <Flex gap={1.2} justifyItems={"center"} alignItems={"center"}>
          <EventNoteIcon color="disabled" />
          <Typography
            sx={{
              fontSize: "17px",
              fontWeight: "medium",
              color: "#1A1A1A",
            }}
          >
            Appointment Details
          </Typography>
        </Flex>
        <ProfileViewer
          mt={2}
          firstColW={3.5}
          secondColW={8.5}
          withoutColn
          data={{
            "Primary Provider": appointmentData?.data?.providerName,
            ...(appointmentData?.data?.mode === "IN_PERSON"
              ? { Location: appointmentData?.data?.locationName }
              : {}),
            "Appointment Status":
              APPOINTMENT_STATUS_RECORD[
                appointmentData?.data?.status as Status
              ],
            "Appointment Type": appointmentData?.data?.type
              ? replaceUnderScoreAddSpace(appointmentData?.data?.type)
              : "-",
            "Reason for Visit": appointmentData?.data?.chiefComplaint ?? "-",
          }}
        />
        <Grid container mt={1}>
          <Grid item xs={3.4}>
            <Text fontWeight={"medium"} color={"#1A1A1A80"}>
              Copay
            </Text>
          </Grid>

          <Grid item paddingLeft={"1rem"} xs={8.6}>
            <Flex gap={1.2} flexDirection={"column"}>
              {appointmentData?.data?.copay ? (
                <Flex gap={1}>
                  <Text sx={{ overflowWrap: "break-word" }}>{`$ ${30}`}</Text>
                  <Typography
                    sx={{ overflowWrap: "break-word", color: "orange" }}
                  >
                    {`(${"pending"})`}
                  </Typography>
                </Flex>
              ) : (
                "-"
              )}
            </Flex>
          </Grid>
        </Grid>
        {(appointmentData?.data?.status === "CANCELLED" ||
          appointmentData?.data?.status === "NO_SHOW") && (
          <>
            <Flex gap={1.2} justifyItems={"center"} alignItems={"center"}>
              <EventNoteIcon color="disabled" />
              <Typography
                sx={{
                  fontSize: "17px",
                  fontWeight: "medium",
                  color: "#1A1A1A",
                }}
              >
                Reason For Cancel
              </Typography>
            </Flex>
            <ProfileViewer
              mt={1}
              firstColW={3.5}
              secondColW={8.5}
              withoutColn
              data={{
                Reason: appointmentData?.data?.cancelReason,
              }}
            />
          </>
        )}
      </Box>
    ),
    insurance_details: (
      <Box position={"relative"} ml={1}>
        {hasUpdateInsurance && (
          <StyledButtonV2
            sx={{
              height: "26px",
              padding: "0 10px",
              position: "absolute",
              top: 0,
              right: 0,
            }}
            label={"Add Insurance"}
            sIcon={<ControlPointOutlinedIcon />}
            variant="text"
            onClick={() => addPatientInsuranceDisclosure.open()}
          />
        )}
        {/* <UpsertInsuranceFormModal
          disClosureHost={addPatientInsuranceDisclosure}
          title="Add Insurance"
        /> */}
        {addPatientInsuranceDisclosure.opened && (
          <AddInsuranceDrower
            opened={addPatientInsuranceDisclosure.opened}
            onClose={addPatientInsuranceDisclosure.close}
            patientId={appointmentData?.data?.patientId}
          />
        )}

        <ProfileViewer
          mt={0}
          firstColW={3.5}
          secondColW={8.5}
          withoutColn
          data={{
            "Insurance Type":
              INSURANCE_TYPE_MAPPER[primaryIns?.orderOfBenefits],
            "Payer Name": primaryIns?.insurancePayer?.payerName,
            "Insurance ID Number": primaryIns?.insuranceId,
          }}
        />
        <EligibilityVerification
          patientId={appointmentData?.data?.patientId}
          primaryIns={appointmentData?.data?.patientPrimaryInsurance}
          providerId={appointmentData?.data?.providerId}
          providerName={appointmentData?.data?.providerName}
        />
      </Box>
    ),
    additional_forms: (
      <Box ml={1}>
        <Flex justifyContent={"end"} width={"100%"} alignItems={"center"}>
          {/* {filteredIntakeSections?.length <= 0 && (
            <>
              <Text c={"#1A1A1A99"} mt={1.5} mb={1}>
                No intake forms added
              </Text>
            </>
          )} */}
          {hasUpdateIntake && (
            <StyledButtonV2
              style={{ height: "26px", padding: "0 10px" }}
              label={showDropdown ? "Close" : "Add Forms"}
              sIcon={
                showDropdown ? (
                  <CloseIcon fontSize="small" />
                ) : (
                  <ControlPointOutlinedIcon fontSize="small" />
                )
              }
              variant="text"
              onClick={() => {
                setShowDropdown(!showDropdown);
              }}
            />
          )}
        </Flex>
        <Grid item container xs={12}>
          {showDropdown ? (
            <Flex flexDirection={"column"} gap={0.5} width={"100%"}>
              <IntakeForms
                assessments={selectedAssessments}
                setAssessments={setSelectedAssessments}
                selectedIntakeForms={selectedIntakeForms}
                setSelectedIntakeForms={setSelectedIntakeForms}
              />
              <Flex width={"100%"} justifyContent={"end"}>
                <Button
                  variant="contained"
                  onClick={() => handleSendAdditionalForm()}
                >
                  Send
                </Button>
              </Flex>
            </Flex>
          ) : (
            <AppointmentAdditionalForms appointmentUuid={appointmentUuid} />
          )}
        </Grid>

        <List sx={{ padding: "0px", margin: "0px" }}>
          {[
            // { label: "Medical Brief Intake Form", isVerified: false },
            // { label: "Nutrition Questions", isVerified: true },
            // { label: "Annual Update", isVerified: false },
            // { label: "Medical Intake Form", isVerified: false },
          ].map((ls: any, ind) => (
            <ListItem key={ls?.label} disableGutters>
              <Flex gap={1}>
                <Text fontWeight={"medium"}>{ind + 1 + ". " + ls?.label}</Text>
                {ls?.isVerified ? (
                  <CheckSharp fontSize="small" color="success" />
                ) : (
                  <Close fontSize="small" color="error" />
                )}
              </Flex>
            </ListItem>
          ))}
        </List>
      </Box>
    ),
  };

  const updateAndNavigateHandler = () => {
    updateStatus({
      appointmentId: appointmentUuid!,
      status: "IN_EXAM",
      xTENANTID: getXtenantId(),
    });

    navigate(
      getPatientVisitNoteUrl(
        appointmentData?.data?.patientId,
        appointmentData?.data?.uuid
      )
      // `${appPaths.ENCOUNTER}?patientid=${appointmentData?.data?.patientId}&appointmentid=${appointmentData?.data?.uuid}`
    );
  };

  const handleStartEncounter = () => {
    if (appointmentData?.data?.mode !== "IN_PERSON") {
      const patientIdParam = appointmentData?.data?.patientId
        ? `&patientid=${appointmentData?.data?.patientId}`
        : "";
      const appointmentIdParam = appointmentData?.data?.uuid
        ? `&appointmentid=${appointmentData?.data?.uuid}`
        : "";

      store.dispatch(
        setCallData({
          patientId: appointmentData?.data?.patientId,
          appointmentId: appointmentData?.data?.uuid,
          patientName: appointmentData?.data?.patientName ?? "",
          providerName: appointmentData?.data?.providerName ?? "",
          startTime: appointmentData?.data?.startTime,
          role: 1,
        })
      );

      navigate(
        `/app/preview?room=${
          appointmentData?.data?.room
        }&role=${1}&providerName=${
          appointmentData?.data?.providerName ?? ""
        }&patientName=${appointmentData?.data?.patientName ?? ""}&startTime=${
          appointmentData?.data?.startTime
        }${patientIdParam}${appointmentIdParam}`
      );
    } else {
      updateAndNavigateHandler();
    }
  };

  // const { hasUpdate: hasUpdateCheckIn } = useRoleController("Check in");

  return (
    <SideSheetDrawer
      sx={{ zIndex: 90 }}
      w="714px"
      open={open}
      title={
        appointmentData?.data?.status === "CONFIRMED"
          ? "Check-In"
          : "Appointment Details"
      }
      onClose={() => {
        onClose?.();
        setAmount("");
        // The patient can pay the emailed Stripe link while this drawer is
        // open, which nothing here can observe. Drop the cached appointment on
        // close so the payment status is re-fetched on the next open instead
        // of needing a page refresh.
        queryClient.invalidateQueries({ queryKey: ["appointment-list"] });
      }}
      footerActions={
        <Flex gap={1} alignItems={"center"}>
          {appointmentData?.data?.status === "CONFIRMED" && (
            <>
              <Button onClick={() => editDemoDisclosure.open()}>
                Edit Patient Details
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  openChangeStatus("NO_SHOW");
                }}
              >
                No Show
              </Button>
              {/* {hasUpdateCheckIn && (
                <Button variant="contained" onClick={openCheckIn}>
                  Start Check In
                </Button>
              )} */}

              <Button
                variant="outlined"
                onClick={() => payDisc.open(appointmentData?.data)}
              >
                Collect Payment
              </Button>

              <Button variant="contained" onClick={handleStartEncounter}>
                Complete Check-In
              </Button>
            </>
          )}
          {appointmentData?.data?.status === "CHECKED_IN" &&
            appointmentData?.data?.mode !== "IN_PERSON" &&
            hasUpdateStartEncounter && (
              <Button variant="contained" onClick={handleStartEncounter}>
                Start Visit
              </Button>
            )}
          {appointmentData?.data?.status === "COMPLETED" && (
            <Button
              variant="contained"
              onClick={() =>
                encounterSummaryDisc.open(appointmentData?.data?.uuid)
              }
            >
              Encounter summary
            </Button>
          )}

          {appointmentData?.data?.status === "IN_EXAM" && (
            <Button variant="contained" onClick={handleStartEncounter}>
              Complete Visit
            </Button>
          )}

          {appointmentData?.data?.status === "PENDING" &&
            hasUpdateAppointment &&
            hasUpdateRescheduleAndCancel && (
              <>
                <Button
                  variant="text"
                  sx={{
                    color: "error.main",
                  }}
                  onClick={() => {
                    openChangeStatus("CANCELLED");
                    queryClient.invalidateQueries({
                      queryKey: ["appointment-list"],
                    });
                    queryClient.invalidateQueries({
                      queryKey: ["appointment-list-v2"],
                    });
                    queryClient.invalidateQueries({
                      queryKey: ["future-appointment"],
                    });
                  }}
                >
                  Cancel Appointment
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    openRescheduleAppointment(appointmentData?.data)
                  }
                >
                  Reschedule Appointment
                </Button>
              </>
            )}

          {appointmentData?.data?.status === "PENDING" && (
            <Button
              variant="outlined"
              onClick={() => payDisc.open(appointmentData?.data)}
            >
              Collect Payment
            </Button>
          )}

          {appointmentData?.data?.status === "PENDING" &&
            hasUpdateStartEncounter && (
              <Button
                variant="contained"
                onClick={() => {
                  updateStatus({
                    appointmentId: appointmentUuid!,
                    status: "CONFIRMED",
                    xTENANTID: getStoredRealm() ?? undefined,
                  }).then((res) => {
                    showSnackbar(getSuccessSnackbar(res));

                    queryClient.invalidateQueries({
                      queryKey: ["appointment-list"],
                    });
                    queryClient.invalidateQueries({
                      queryKey: ["appointment-list-v2"],
                    });
                    // setTimeout(() => {
                    //   onClose?.();
                    // }, 1500);
                  });
                }}
              >
                Confirm Appointment
              </Button>
            )}
        </Flex>
      }
    >
      <ViewEncounterSummaryDrawer disclosure={encounterSummaryDisc} />

      <UpsertEditCheckInPatient
        disclosureHost={editDemoDisclosure}
        patientData={patientData}
      />

      <Elements stripe={getStripePromise()}>
        <PaymentDrawer
          disclosureHost={payDisc}
          paymentCategory={
            appointmentData?.data?.paymentType === "INSURANCE"
              ? "COPAY"
              : "SELF_PAY"
          }
          amount={amount}
          providerId={
            (appointmentData as any)?.data?.providerId
              ? {
                  uuid: (appointmentData as any)?.data?.providerId,
                  name: (appointmentData as any)?.data?.providerName ?? "",
                }
              : undefined
          }
        />
      </Elements>

      <Box mb={1}>
        {/* Patient Header Section */}
        <Box
          p={1.5}
          sx={{
            borderRadius: 1,
            border: "1px solid #E0E0E0",
          }}
        >
          <Flex alignItems={"center"} gap={2}>
            <Avatar
              src={appointmentData?.data?.avatar ?? DEFAULT_USER_SRC}
              sx={{ width: 56, height: 56 }}
            />
            <Box flex={1}>
              <Flex alignItems={"center"} justifyContent={"space-between"}>
                <Typography
                  variant="h4"
                  fontWeight={"medium"}
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    navigate(
                      `/app/provider/patients/new-patient/${appointmentData?.data?.patientId}/index`
                    );
                    onClose?.();
                  }}
                >
                  {appointmentData?.data?.patientName}{" "}
                  {appointmentData?.data?.ecarehealthPatientId
                    ? `(${appointmentData?.data?.ecarehealthPatientId})`
                    : ""}
                </Typography>
                {appointmentData?.data?.mode !== "IN_PERSON" && (
                  <Chip
                    sx={{
                      bgcolor: "#F3E8FF",
                      borderColor: "#7C3AED",
                    }}
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <VideocamOutlinedIcon
                          fontSize="small"
                          sx={{
                            color: "#7C3AED",
                          }}
                        />
                        <Text c="#7C3AED">Telehealth</Text>
                      </Box>
                    }
                    variant="outlined"
                    size="small"
                  />
                )}
                {appointmentData?.data?.mode === "IN_PERSON" && (
                  <Chip
                    sx={{
                      bgcolor: "#F3E8FF",
                      borderColor: "#7C3AED",
                    }}
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PersonOutlinedIcon
                          fontSize="small"
                          sx={{
                            color: "#7C3AED",
                          }}
                        />
                        <Text c="#7C3AED">In-person</Text>
                      </Box>
                    }
                    variant="outlined"
                    size="small"
                  />
                )}
              </Flex>
              <Flex alignItems={"center"} gap={1} mt={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(appointmentData?.data?.patientDob)}
                </Typography>
                <Box
                  sx={{
                    width: "1px",
                    height: "12px",
                    bgcolor: "#E0E0E0",
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {getAge(appointmentData?.data?.patientDob)} yrs
                </Typography>
                <Box
                  sx={{
                    width: "1px",
                    height: "12px",
                    bgcolor: "#E0E0E0",
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {appointmentData?.data?.patientGender
                    ? GENDER_RECORD[appointmentData?.data?.patientGender]
                    : "-"}
                </Typography>
              </Flex>
              <Flex gap={3} mt={0.5}>
                <Flex
                  alignItems={"center"}
                  gap={0.5}
                  onClick={handleCallPatient}
                  sx={{ cursor: "pointer" }}
                >
                  <PhoneOutlinedIcon
                    fontSize="small"
                    sx={{ color: CHART_COLORS.primary }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: CHART_COLORS.primary }}
                  >
                    {formatPhoneNumber(
                      appointmentData?.data?.patientPhone ?? ""
                    )}
                  </Typography>
                </Flex>
                <Flex
                  alignItems={"center"}
                  gap={0.5}
                  onClick={handleEmailPatient}
                  sx={{ cursor: "pointer" }}
                >
                  <EmailOutlinedIcon
                    fontSize="small"
                    sx={{ color: CHART_COLORS.primary }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: CHART_COLORS.primary }}
                  >
                    {appointmentData?.data?.patientEmail}
                  </Typography>
                </Flex>
              </Flex>
              {contactDialDisclosure.opened && (
                <ContactDialModal disclosureHost={contactDialDisclosure} />
              )}
              {sendMailDisclosure.opened && (
                <SendBulkEmailModal
                  disClosureHost={sendMailDisclosure}
                  disableAddPatient
                />
              )}
            </Box>
          </Flex>
        </Box>
        <Divider />
        {/* Location and Time Section */}
        <Box
          p={1.5}
          mt={1}
          sx={{
            borderRadius: 1,
            border: "1px solid #E0E0E0",
          }}
        >
          {appointmentData?.data?.mode === "IN_PERSON" && (
            <Flex alignItems={"center"} justifyContent={"space-between"} mb={1}>
              <Flex alignItems={"center"} gap={1}>
                <LocationOnOutlinedIcon fontSize="small" />
                <Text>{appointmentData?.data?.locationName || "-"}</Text>
              </Flex>
              <Flex
                bgcolor={COLOR_MAPPER[label as Action]?.bgColor as any}
                border={`1px solid ${
                  COLOR_MAPPER[label as Action]?.color as any
                }`}
                justifyContent={"space-between"}
                borderRadius={50}
                overflow={"hidden"}
                gap={1}
                px={1}
                alignItems={"center"}
                height={"28px"}
              >
                <Text c={COLOR_MAPPER[label]?.color}>{label}</Text>
              </Flex>
            </Flex>
          )}
          {appointmentData?.data?.mode !== "IN_PERSON" && (
            <Flex alignItems={"center"} justifyContent={"space-between"} mb={1}>
              <Flex alignItems={"center"} gap={1}>
                <LocationOnOutlinedIcon fontSize="small" />
              </Flex>
              <Flex
                bgcolor={COLOR_MAPPER[label as Action]?.bgColor as any}
                border={`1px solid ${
                  COLOR_MAPPER[label as Action]?.color as any
                }`}
                justifyContent={"space-between"}
                borderRadius={50}
                overflow={"hidden"}
                gap={1}
                px={1}
                alignItems={"center"}
                height={"28px"}
              >
                <Text c={COLOR_MAPPER[label]?.color}>{label}</Text>
              </Flex>
            </Flex>
          )}
          <Flex alignItems={"center"} gap={1}>
            <InsertInvitationIcon fontSize="small" />
            <Text>
              {appointmentData?.data?.startTime
                ? formatDate(appointmentData?.data?.startTime)
                : "-"}
            </Text>
          </Flex>
          <Flex alignItems={"center"} gap={1} mb={1}>
            <ScheduleIcon fontSize="small" />
            <Text>
              {appointmentData?.data?.startTime
                ? formatTime(appointmentData?.data?.startTime, {
                    // withSeconds: true,
                  }) +
                  " - " +
                  formatTime(
                    appointmentData?.data?.endTime ||
                      appointmentData?.data?.startTime
                  ) +
                  ` ${appointmentData?.data?.timezone ? `(${appointmentData?.data?.timezone})` : ""}`
                : "-"}
            </Text>
          </Flex>
        </Box>
        <Divider />

        {/* Appointment Details Section */}
        <CustomAccordion
          onClickHandler={() => {}}
          showExpandIcon={false}
          accordionSummeryProps={{
            style: {
              flexDirection: "row-reverse",
              padding: 0,
              backgroundColor: DEFAULT_BG_COLOR_2,
            },
          }}
          accordionProps={
            {
              sx: {
                border: "1px solid #E0E0E0",
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "none",
                "&:before": {
                  display: "none",
                },
              },
            } as any
          }
          expanded={true}
          accordionSummaryContent={
            <Text color="black" ml={1} fontWeight={"medium"}>
              Appointment Details
            </Text>
          }
          accordionDetailsContent={
            <Flex gap={1.2} flexDirection={"column"} padding={"8px"}>
              <ProfileViewer
                gap={"5px"}
                withoutColn
                firstColW={3}
                secondColW={7}
                data={{
                  Provider: appointmentData?.data?.providerName,
                  "Appointment Type": appointmentData?.data?.type ?? "",
                  // "Exam Room": appointmentData?.data?.examRoom || "-",
                  "Chief Complaint":
                    appointmentData?.data?.chiefComplaint || "",
                  // need not to populate as per req 5101
                  ...(appointmentData?.data?.cancelReason && {
                    "Cancellation Reason":
                      appointmentData?.data?.cancelReason || "",
                  }),
                }}
              />

              <Grid container>
                <Grid item xs={3.1}>
                  <Text
                    fontWeight={"medium"}
                    color={"#1A1A1A80"}
                    fontSize={DEFAULT_FONTSIZE}
                  >
                    {appointmentData?.data?.paymentType === "INSURANCE"
                      ? "Copay"
                      : "SelfPay"}
                  </Text>
                </Grid>
                {hasUpdateAppointment && (
                  <Flex gap={2} alignItems={"center"}>
                    <Box width={"120px"}>
                      <NumberInput
                        label="Amount"
                        blockedKeys={["-", "+", "e", "E"]}
                        textFieldProps={{
                          value: amount,
                          onChange: (e) => setAmount(e.target.value),
                          // Currency symbol so the copay input matches how
                          // amounts are rendered everywhere else. The adornment
                          // renders as body1 (16px) by default, which outsizes
                          // the 14px input value, so keep it a touch smaller.
                          InputProps: {
                            startAdornment: (
                              <InputAdornment
                                position="start"
                                sx={{
                                  "& .MuiTypography-root": {
                                    fontSize: "13px",
                                  },
                                }}
                              >
                                $
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>
                    <Tooltip
                      title={"amount is required for payment link"}
                      followCursor
                    >
                      <Link
                        style={{ cursor: "pointer" }}
                        onClick={handleSendPaymentLink}
                      >
                        <Text>{`${appointmentData?.data?.paymentLinkSent ? "Resend" : "Send"} payment Link via Email`}</Text>
                      </Link>
                    </Tooltip>
                  </Flex>
                )}
              </Grid>

              {hasPaymentLink(appointmentData?.data) && (
                <Grid container alignItems={"center"}>
                  <Grid item xs={3.1}>
                    <Text
                      fontWeight={"medium"}
                      color={"#1A1A1A80"}
                      fontSize={DEFAULT_FONTSIZE}
                    >
                      Payment Link Sent
                    </Text>
                  </Grid>
                  <PaymentLinkDetails
                    paymentLinkAmount={appointmentData?.data?.paymentLinkAmount}
                    paymentLinkPaid={appointmentData?.data?.paymentLinkPaid}
                  />
                </Grid>
              )}
            </Flex>
          }
        />
        <Divider />

        <Box>
          {/* Insurance Details Section */}
          {roleFilter({
            list: [
              {
                label: "Insurance Details",
                value: "insurance_details",
                icon: <HealthAndSafetyIcon />,
              },
              {
                label: "Additional Forms",
                value: "additional_forms",
                icon: <TaskOutlinedIcon />,
              },
            ],
            compWith: {
              insurance_details: hasViewInsurance,
              additional_forms: hasViewIntake,
            },
          }).map((item, index) => (
            <>
              <CustomAccordion
                onClickHandler={() => {
                  setExpandedSections((prev) => {
                    const newExpandedSections = [...prev];
                    newExpandedSections[index] = !newExpandedSections[index];
                    return newExpandedSections;
                  });
                }}
                accordionSummeryProps={{
                  style: {
                    padding: 0,
                    backgroundColor: DEFAULT_BG_COLOR_2,
                  },
                }}
                accordionProps={
                  {
                    sx: {
                      border: "1px solid #E0E0E0",
                      borderRadius: 1,
                      overflow: "hidden",
                      boxShadow: "none",
                      "&:before": {
                        display: "none",
                      },
                    },
                  } as any
                }
                expanded={expandedSections[index]}
                accordionSummaryContent={
                  <Flex alignItems={"center"} gap={1} ml={1}>
                    {/* {item.icon} */}
                    <Text fontWeight={"medium"} color="black">
                      {item.label}
                    </Text>
                  </Flex>
                }
                accordionDetailsContent={
                  <Box p={0.5}>{ACTIVE_COMP[item.value]}</Box>
                }
              />

              <Divider />
            </>
          ))}
        </Box>

        <LoadingOverlay isLoading={loadingAppointment} />
      </Box>
    </SideSheetDrawer>
  );
};

export default ViewAppointment;

export const BaseBox = ({ children }: { children: React.ReactNode }) => (
  <Box bgcolor={"#FAFAFA"} p={1.2}>
    {children}
  </Box>
);
